// src/components/calculators/CalculatorContext.js
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Commission tiers - matching your exact structure
const COMMISSION_TIERS = [
  { limit: 3000, rate: 0.14, displayRate: 14 },
  { limit: 7000, rate: 0.11, displayRate: 11 },
  { limit: 15000, rate: 0.08, displayRate: 8 },
  { limit: Infinity, rate: 0.05, displayRate: 5 },
];

const CARD_FEE_RATE = 0.018; // 1.8% as per your specification
const DEFAULT_VAT_RATE = 20;
const MAX_ITERATIONS = 200; // Increased for better convergence
const TOLERANCE = 0.001; // Tightened to £0.001 for better accuracy

const CalculatorContext = createContext();

export const useCalculator = () => {
  const context = useContext(CalculatorContext);
  if (!context) {
    throw new Error('useCalculator must be used within CalculatorProvider');
  }
  return context;
};

export const CalculatorProvider = ({ children }) => {
  // Quick Check State
  const [quickCheckData, setQuickCheckData] = useState({
    sellingPrice: '',
    vatRate: DEFAULT_VAT_RATE.toString(),
    applyVAT: false
  });

  // Quote Builder State
  const [quoteBuilderData, setQuoteBuilderData] = useState({
    costOfGoods: '',
    targetProfit: '',
    vatRate: DEFAULT_VAT_RATE.toString(),
    applyVAT: false
  });

  // Calculate marginal commission based on tiers
  const calculateMarginalCommission = useCallback((price) => {
    let totalCommission = 0;
    let previousLimit = 0;
    const validPrice = Math.max(0, price);

    for (const tier of COMMISSION_TIERS) {
      if (validPrice > previousLimit) {
        const taxableAmountInTier = Math.min(validPrice, tier.limit) - previousLimit;
        totalCommission += taxableAmountInTier * tier.rate;
      }
      previousLimit = tier.limit;
      if (validPrice <= tier.limit) break;
    }
    return parseFloat(totalCommission.toFixed(2));
  }, []);

  // Get commission breakdown for display
  const getCommissionBreakdown = useCallback((price) => {
    const breakdown = [];
    let previousLimit = 0;
    const validPrice = Math.max(0, price);

    for (const tier of COMMISSION_TIERS) {
      if (validPrice > previousLimit) {
        const amountInTier = Math.min(validPrice, tier.limit) - previousLimit;
        const commissionInTier = parseFloat((amountInTier * tier.rate).toFixed(2));
        
        if (amountInTier > 0.001 && commissionInTier > 0) {
          breakdown.push({
            range: `£${previousLimit.toLocaleString()} - £${tier.limit === Infinity ? 'Above' : tier.limit.toLocaleString()}`,
            amount: amountInTier,
            rate: tier.displayRate,
            commission: commissionInTier,
          });
        }
      }
      previousLimit = tier.limit;
      if (validPrice <= tier.limit) break;
    }
    return breakdown;
  }, []);

  // Calculate results from selling price
  const calculateResultsFromSellingPrice = useCallback((sellingPrice, costOfGoods = 0, vatRate = 0, applyVAT = false) => {
    const price = parseFloat(sellingPrice) || 0;
    const cost = parseFloat(costOfGoods) || 0;
    const rate = parseFloat(vatRate) || 0;

    // IMPORTANT: Commission and card fees are calculated on the FULL VAT-inclusive price
    // This is how marketplaces work - they take their cut from the full amount
    const totalCommission = calculateMarginalCommission(price);
    const cardFees = parseFloat((price * CARD_FEE_RATE).toFixed(2));

    // VAT calculation - if VAT is included in the price
    let vatAmount = 0;
    let priceBeforeVAT = price;
    if (applyVAT && rate > 0) {
      // Extract VAT from the inclusive price
      // If price is £120 inc VAT at 20%, then VAT = £20, ex-VAT price = £100
      vatAmount = parseFloat((price - (price / (1 + rate / 100))).toFixed(2));
      priceBeforeVAT = parseFloat((price / (1 + rate / 100)).toFixed(2));
    }

    // What the jeweller receives before remitting VAT to HMRC
    const netBeforeVATRemittance = parseFloat((price - totalCommission - cardFees).toFixed(2));
    
    // What the jeweller keeps after remitting VAT to HMRC
    // This is their actual take-home amount
    const netAfterVATRemittance = parseFloat((netBeforeVATRemittance - vatAmount).toFixed(2));
    
    // Gross profit after all deductions including cost of goods
    const grossProfit = parseFloat((netAfterVATRemittance - cost).toFixed(2));

    return {
      sellingPrice: price,
      priceBeforeVAT,
      totalCommission,
      commissionBreakdown: getCommissionBreakdown(price),
      cardFees,
      vatAmount,
      vatRateApplied: applyVAT ? rate : 0,
      netBeforeVATRemittance,
      netAfterVATRemittance,
      costOfGoods: cost,
      grossProfit,
    };
  }, [calculateMarginalCommission, getCommissionBreakdown]);

  // Find selling price for target profit (iterative solver)
  // This finds the price needed to achieve target profit, then adds VAT on top
  const findSellingPriceForTargetProfit = useCallback((
    targetProfit,
    costOfGoods,
    vatRate = 0,
    applyVAT = false,
    maxIterations = MAX_ITERATIONS,
    tolerance = TOLERANCE
  ) => {
    const profitTarget = parseFloat(targetProfit) || 0;
    const cost = parseFloat(costOfGoods) || 0;
    
    // The jeweller needs to end up with: cost + profit (after all deductions)
    const targetRevenueAfterAllDeductions = profitTarget + cost;

    if (targetRevenueAfterAllDeductions <= 0 && profitTarget <= 0 && cost <= 0) return 0;
    if (targetRevenueAfterAllDeductions < 0) return 0;

    // Initial estimate for the base price (before VAT)
    const avgCommissionRate = COMMISSION_TIERS[0].rate;
    let guessPrice = targetRevenueAfterAllDeductions / (1 - avgCommissionRate - CARD_FEE_RATE);
    if (guessPrice <= 0) {
      guessPrice = targetRevenueAfterAllDeductions;
    }

    let lowerBound = 0;
    let upperBound = Math.max(guessPrice * 5, targetRevenueAfterAllDeductions + 1000);

    // Binary search to find the right base price
    for (let i = 0; i < maxIterations; i++) {
      // If VAT applies, add it on top of the base price
      // This is the price the customer will actually pay
      const sellingPriceWithVAT = applyVAT && vatRate > 0 
        ? guessPrice * (1 + parseFloat(vatRate) / 100)
        : guessPrice;
      
      // Commission and card fees are on the full price including VAT
      const currentCommission = calculateMarginalCommission(sellingPriceWithVAT);
      const currentCardFees = sellingPriceWithVAT * CARD_FEE_RATE;
      
      // Calculate VAT amount that must be remitted to HMRC
      const currentVatAmount = applyVAT && vatRate > 0
        ? sellingPriceWithVAT - guessPrice  // VAT is the difference between VAT-inclusive and base price
        : 0;

      // What the jeweller actually keeps after all deductions
      const netRevenue = sellingPriceWithVAT - currentCommission - currentCardFees - currentVatAmount;
      
      // Check if we've hit our target
      const difference = netRevenue - targetRevenueAfterAllDeductions;

      if (Math.abs(difference) <= tolerance) {
        return parseFloat(sellingPriceWithVAT.toFixed(2));
      }

      // Adjust bounds for next iteration
      if (difference < 0) {
        lowerBound = guessPrice;
      } else {
        upperBound = guessPrice;
      }
      guessPrice = (lowerBound + upperBound) / 2;

      // Check if we're stuck
      if (upperBound - lowerBound < tolerance / 100) {
        const finalPrice = applyVAT && vatRate > 0 
          ? guessPrice * (1 + parseFloat(vatRate) / 100)
          : guessPrice;
        return parseFloat(finalPrice.toFixed(2));
      }
    }

    // Return best guess if didn't converge
    const finalPrice = applyVAT && vatRate > 0 
      ? guessPrice * (1 + parseFloat(vatRate) / 100)
      : guessPrice;
    return parseFloat(finalPrice.toFixed(2));
  }, [calculateMarginalCommission]);

  // Quick Check Results
  const quickCheckResults = useMemo(() => {
    const price = parseFloat(quickCheckData.sellingPrice);
    if (!price || price <= 0) return null;

    return calculateResultsFromSellingPrice(
      price, 
      0, 
      quickCheckData.vatRate, 
      quickCheckData.applyVAT
    );
  }, [quickCheckData, calculateResultsFromSellingPrice]);

  // Quote Builder Results
  const quoteBuilderResults = useMemo(() => {
    const cost = parseFloat(quoteBuilderData.costOfGoods);
    const profit = parseFloat(quoteBuilderData.targetProfit);
    
    if (!quoteBuilderData.costOfGoods && !quoteBuilderData.targetProfit) return null;
    if (cost < 0 || profit < 0) return null;

    const finalSellingPrice = findSellingPriceForTargetProfit(
      profit,
      cost,
      quoteBuilderData.vatRate,
      quoteBuilderData.applyVAT
    );

    if (finalSellingPrice > 0 || (cost === 0 && profit === 0 && finalSellingPrice === 0)) {
      // For Quote Builder, if VAT is applied, it's added ON TOP of the base price
      // So we need to calculate differently than Quick Check
      if (quoteBuilderData.applyVAT && parseFloat(quoteBuilderData.vatRate) > 0) {
        const vatRate = parseFloat(quoteBuilderData.vatRate) / 100;
        const basePrice = finalSellingPrice / (1 + vatRate);
        const vatAmount = finalSellingPrice - basePrice;
        
        // Commission and fees are on the full VAT-inclusive price
        const totalCommission = calculateMarginalCommission(finalSellingPrice);
        const cardFees = parseFloat((finalSellingPrice * CARD_FEE_RATE).toFixed(2));
        
        // What the jeweller receives and keeps
        const netBeforeVATRemittance = parseFloat((finalSellingPrice - totalCommission - cardFees).toFixed(2));
        const netAfterVATRemittance = parseFloat((netBeforeVATRemittance - vatAmount).toFixed(2));
        const grossProfit = parseFloat((netAfterVATRemittance - cost).toFixed(2));
        
        return {
          sellingPrice: finalSellingPrice,
          priceBeforeVAT: basePrice,
          totalCommission,
          commissionBreakdown: getCommissionBreakdown(finalSellingPrice),
          cardFees,
          vatAmount,
          vatRateApplied: parseFloat(quoteBuilderData.vatRate),
          netBeforeVATRemittance,
          netAfterVATRemittance,
          costOfGoods: cost,
          grossProfit,
        };
      } else {
        // No VAT - use standard calculation
        return calculateResultsFromSellingPrice(
          finalSellingPrice,
          cost,
          0,
          false
        );
      }
    }

    return null;
  }, [quoteBuilderData, findSellingPriceForTargetProfit, calculateResultsFromSellingPrice, calculateMarginalCommission, getCommissionBreakdown]);

  // Validation functions
  const validateInput = (value) => {
    return /^\d*\.?\d{0,2}$/.test(value);
  };

  const validateVATRate = (value) => {
    return /^(\d{1,2}(\.\d*)?|100(\.0*)?)$/.test(value) || value === '';
  };

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '£0.00';
    }
    return `£${value.toLocaleString('en-GB', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0%';
    }
    return `${value.toFixed(1)}%`;
  };

  const value = {
    // State
    quickCheckData,
    setQuickCheckData,
    quoteBuilderData,
    setQuoteBuilderData,
    
    // Results
    quickCheckResults,
    quoteBuilderResults,
    
    // Constants
    COMMISSION_TIERS,
    CARD_FEE_RATE,
    DEFAULT_VAT_RATE,
    
    // Functions
    calculateCommission: calculateMarginalCommission,
    validateInput,
    validateVATRate,
    formatCurrency,
    formatPercentage
  };

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
};

export default CalculatorContext;